import React, { useState } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  StyledAutocomplete,
  StyledEllipsisBox,
  StyledListItem,
  StyledTagBox,
  StyledTextField,
} from './Style/SearchableDropDownV2.style';
import {
  OptionType,
  SearchableDropDownPropsWithOption,
} from '../../helpers/types';

const SearchableDropDownV2 = ({
  items,
  size,
  placeholder,
  label,
  onChange,
  selectedValues,
  multiple = false,
  dataTestId,
  clearIcon = false,
  minWidth = 150,
  maxWidth = 400,
}: SearchableDropDownPropsWithOption & {
  minWidth?: number | string;
  maxWidth?: number | string;
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  const renderCommaSeparatedValues = (values: OptionType[]): string => {
    return values.map(option => option?.label ?? '').join(', ');
  };

  const formatMultipleDropdownOptions = (
    items: { label: string; value: string }[],
  ): OptionType[] => {
    return Array.from(new Set(items?.map(item => item?.label) ?? []))
      .sort((a, b) => a.localeCompare(b))
      .map(label => {
        const correspondingItem = items.find(item => item.label === label);
        return {
          label: correspondingItem?.label ?? '',
          value: correspondingItem?.value ?? '',
        };
      });
  };

  const shouldDisplayPlaceholder =
    !selectedValues ||
    (Array.isArray(selectedValues) && selectedValues.length === 0);

  return (
    <Box sx={{ display: 'inline-block', minWidth, maxWidth, width: '100%' }}>
      <StyledAutocomplete
        sx={{
          width: '100%',
          minWidth,
          maxWidth,
        }}
        multiple={multiple}
        data-testid={dataTestId}
        getOptionLabel={option => option?.label ?? ''}
        disableCloseOnSelect={multiple}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={selectedValues ?? (multiple ? [] : null)}
        options={formatMultipleDropdownOptions(items)}
        filterOptions={(options, { inputValue }) => {
          const lowerInput = inputValue.toLowerCase();
          return options
            .filter(option => {
              const lowerLabel = option?.label.toLowerCase() ?? '';
              return (
                lowerLabel.startsWith(lowerInput) ||
                lowerLabel.includes(`-${lowerInput}`)
              );
            })
            .slice(0, 100);
        }}
        isOptionEqualToValue={(option: OptionType, value: OptionType) =>
          option?.value === value?.value
        }
        onChange={(event, value) => {
          onChange(event, value);
        }}
        freeSolo={false}
        renderOption={(props, option) => {
          const isDisabled = option?.disabled;
          return (
            <StyledListItem
              {...props}
              key={option?.label ?? ''}
              style={{
                pointerEvents: isDisabled ? 'none' : 'auto',
                color: isDisabled
                  ? theme.palette.text.disabled
                  : theme.palette.text.primary,
                minWidth,
                maxWidth,
              }}
              aria-disabled={isDisabled}
              disableGutters
            >
              {option?.label ?? ''}
            </StyledListItem>
          );
        }}
        renderInput={params => (
          <StyledTextField
            {...params}
            variant="outlined"
            label={label}
            placeholder={shouldDisplayPlaceholder ? placeholder : ''}
            size={size}
            onClick={() => setOpen(true)}
          />
        )}
        renderTags={value => {
          if (multiple) {
            return (
              <StyledTagBox>{renderCommaSeparatedValues(value)}</StyledTagBox>
            );
          }
          if (value?.length === 0) {
            return value?.map(option => (
              <StyledTagBox key={option?.value ?? option?.label}>
                {option?.label}
              </StyledTagBox>
            ));
          } else {
            return (
              <Box
                sx={{
                  display: 'inline-flex',
                  flexWrap: 'nowrap',
                  alignItems: 'center',
                }}
              >
                <StyledTagBox key={value[0]?.value ?? value[0]?.label}>
                  {value[0]?.label}
                </StyledTagBox>
                <StyledEllipsisBox>...</StyledEllipsisBox>
              </Box>
            );
          }
        }}
        clearIcon={clearIcon}
      />
    </Box>
  );
};

SearchableDropDownV2.defaultProps = {
  multiple: false,
  minWidth: 150,
  maxWidth: 400,
};

export default SearchableDropDownV2;
